/**
 * Redesigned careers pages (views/redesign/pages/about/careers.ejs and
 * careers-opening.ejs).
 *
 *  - "Apply for this position" buttons ([data-position]) pick that position in
 *    the form's Position list before the page scrolls to #apply.
 *  - The application form (#careersForm) posts natively as multipart/form-data
 *    (for the résumé) to /about/careers/apply. Before it goes, this checks the
 *    fields against the same rules the server applies (read from the form's
 *    required/maxlength attributes and data-max-file-size / data-allowed-types,
 *    which the controller fills from the careers form config), shows errors
 *    next to each field, and adds a reCAPTCHA v3 token as a hidden
 *    recaptchaToken field: the server rejects any submission without one.
 */

import { orgContactPhrase } from '../../utils/orgContact.js';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fieldLabel(el) {
    const label = el.closest('label');
    if (!label) return el.name;
    const text = [...label.childNodes].filter(n => n.nodeType === 3).map(n => n.textContent).join(' ');
    return text.replace(/\s+/g, ' ').trim() || el.name;
}

/** The extension of a file name, lowercased with its dot (".pdf"), or "". */
export function fileExtension(name) {
    const m = /\.[^.]+$/.exec(String(name || ''));
    return m ? m[0].toLowerCase() : '';
}

/**
 * The error for one field, or '' when it is fine.
 * rule: { label, required, type, minLength, maxLength, maxFileSize, allowedTypes }
 * value: a string, or for a file input { name, size } (null when none chosen).
 */
export function fieldError(rule, value) {
    const label = rule.label;
    if (rule.type === 'file') {
        if (!value) return rule.required ? `Please attach your ${label.toLowerCase()}.` : '';
        const ext = fileExtension(value.name);
        if (rule.allowedTypes?.length && !rule.allowedTypes.includes(ext)) {
            return `Please attach a ${rule.allowedTypes.join(', ')} file.`;
        }
        if (rule.maxFileSize && value.size > rule.maxFileSize) {
            return `Please attach a file under ${Math.round(rule.maxFileSize / (1024 * 1024))} MB.`;
        }
        return '';
    }
    const v = String(value ?? '').trim();
    if (!v) return rule.required ? `Please enter your ${label.toLowerCase()}.` : '';
    if (rule.minLength && v.length < rule.minLength) return `${label} must be at least ${rule.minLength} characters.`;
    if (rule.maxLength && v.length > rule.maxLength) return `${label} must be ${rule.maxLength} characters or fewer.`;
    if (rule.type === 'email' && !EMAIL.test(v)) return 'Please enter a valid email address.';
    if (rule.type === 'tel' && v.replace(/\D/g, '').length < 10) return 'Please enter a phone number with area code.';
    return '';
}

function ruleFor(el, form) {
    const max = parseInt(el.getAttribute('maxlength'), 10);
    const min = parseInt(el.getAttribute('minlength'), 10);
    return {
        label: el.name === 'message' ? 'Message' : el.name === 'attachment' ? 'Résumé' : fieldLabel(el),
        required: el.required,
        type: el.type === 'file' ? 'file' : el.type,
        minLength: Number.isFinite(min) ? min : 0,
        maxLength: Number.isFinite(max) ? max : 0,
        maxFileSize: parseInt(form.dataset.maxFileSize, 10) || 0,
        allowedTypes: (form.dataset.allowedTypes || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
    };
}

function valueOf(el) {
    if (el.type === 'file') {
        const f = el.files && el.files[0];
        return f ? { name: f.name, size: f.size } : null;
    }
    return el.value;
}

function setError(el, message) {
    const label = el.closest('label') || el.parentNode;
    let err = label.querySelector('.f-err');
    if (!message) {
        el.removeAttribute('aria-invalid');
        if (err) {
            const ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== err.id);
            if (ids.length) el.setAttribute('aria-describedby', ids.join(' '));
            else el.removeAttribute('aria-describedby');
            err.remove();
        }
        return;
    }
    if (!err) {
        err = document.createElement('span');
        err.className = 'f-err';
        err.id = `careers-${el.name}-err`;
        label.appendChild(err);
        const ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        el.setAttribute('aria-describedby', [...ids, err.id].join(' '));
    }
    err.textContent = message;
    el.setAttribute('aria-invalid', 'true');
}

function fields(form) {
    return [...form.querySelectorAll('input[name], select[name], textarea[name]')]
        .filter(el => el.type !== 'hidden' && !el.closest('.hp-trap'));
}

/** Checks every field; marks the bad ones and returns the first, or null. */
export function validateForm(form) {
    let first = null;
    fields(form).forEach(el => {
        const msg = fieldError(ruleFor(el, form), valueOf(el));
        setError(el, msg);
        if (msg && !first) first = el;
    });
    return first;
}

export function initPositionButtons(root = document) {
    const select = root.querySelector('#careersPosition');
    if (!select) return;
    root.querySelectorAll('[data-position]').forEach(btn => {
        btn.addEventListener('click', () => {
            const value = btn.dataset.position;
            if ([...select.options].some(o => o.value === value)) select.value = value;
        });
    });
}

export function initMessageCounter(root = document) {
    const form = root.querySelector('#careersForm');
    const area = form?.querySelector('textarea[name="message"]');
    const count = form?.querySelector('.f__count');
    if (!area || !count) return;
    const max = parseInt(count.dataset.max, 10) || 0;
    const update = () => {
        const left = max - area.value.length;
        count.textContent = `${left} of ${max} characters left`;
        count.classList.toggle('is-near', left <= 25);
    };
    area.addEventListener('input', update);
    update();
}

function loadRecaptcha(siteKey) {
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    document.head.appendChild(script);
}

async function recaptchaToken(siteKey, waitMs = 8000) {
    const start = Date.now();
    while (typeof window.grecaptcha === 'undefined') {
        if (Date.now() - start > waitMs) return null;
        await new Promise(r => setTimeout(r, 50));
    }
    try {
        return await new Promise((resolve, reject) => {
            window.grecaptcha.ready(() => {
                window.grecaptcha.execute(siteKey, { action: 'careers_apply' }).then(resolve, reject);
            });
        });
    } catch {
        return null;
    }
}

export function initCareersForm(root = document, { getToken = recaptchaToken } = {}) {
    const form = root.querySelector('#careersForm');
    if (!form || form.dataset.careersInitialized === 'true') return;
    form.dataset.careersInitialized = 'true';

    const status = form.querySelector('.careers-form__status');
    const button = form.querySelector('button[type="submit"]');
    const siteKey = form.dataset.recaptchaSiteKey;
    if (siteKey) loadRecaptcha(siteKey);

    function showStatus(message) {
        if (!status) return;
        status.textContent = message;
        status.hidden = !message;
    }

    // Re-check a field once it has been marked wrong, so the error clears as it is fixed.
    form.addEventListener('input', e => {
        if (e.target.getAttribute?.('aria-invalid') === 'true') setError(e.target, fieldError(ruleFor(e.target, form), valueOf(e.target)));
    });
    form.addEventListener('change', e => {
        if (e.target.type === 'file' || e.target.getAttribute?.('aria-invalid') === 'true') {
            setError(e.target, fieldError(ruleFor(e.target, form), valueOf(e.target)));
        }
    });

    form.addEventListener('submit', async e => {
        e.preventDefault();
        showStatus('');
        const bad = validateForm(form);
        if (bad) {
            bad.focus();
            return;
        }
        if (siteKey) {
            const label = button?.textContent;
            if (button) {
                button.disabled = true;
                button.textContent = 'Sending…';
            }
            const token = await getToken(siteKey);
            if (!token) {
                if (button) {
                    button.disabled = false;
                    button.textContent = label;
                }
                showStatus(
                    "We couldn't complete the security check for this form. An ad blocker or privacy extension " +
                    "may be blocking Google's verification script. Turn it off and try again, or reach us at " +
                    `${orgContactPhrase()}.`
                );
                return;
            }
            let field = form.querySelector('input[name="recaptchaToken"]');
            if (!field) {
                field = document.createElement('input');
                field.type = 'hidden';
                field.name = 'recaptchaToken';
                form.appendChild(field);
            }
            field.value = token;
        }
        // HTMLFormElement.submit() does not fire 'submit', so this does not re-enter.
        form.submit();
    });
}

function init() {
    initPositionButtons();
    initMessageCounter();
    initCareersForm();
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}
